import {
  UserType,
  FlaggedProductsResponse,
  UpdateNormalizedProductRequest,
  UpdateUserRequest,
} from '@/lib/types';
import { environment } from '../environments/environment';
import { UserStatus } from '@/lib/enums';

// Base API configuration
export const API_BASE_URL = environment.apiUrl;

// Token management
export const getToken = (): string | null => {
  return localStorage.getItem('token');
};

export const setToken = (token: string): void => {
  localStorage.setItem('token', token);
};

export const removeToken = (): void => {
  localStorage.removeItem('token');
};

// Decode JWT token to get user info
export const decodeToken = (token: string): any => {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map(function (c) {
          return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
        })
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch (error) {
    console.error('Token decode failed:', error);
    return null;
  }
};

// Generic fetch wrapper with error handling and JWT support
export async function apiRequest<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const url = `${API_BASE_URL}${endpoint}`;
  const token = getToken();

  const defaultOptions: RequestInit = {
    headers: {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
      ...options.headers,
    },
    ...options,
  };

  try {
    const response = await fetch(url, defaultOptions);

    if (response.status === 401) {
      // Token expired or invalid
      removeToken();
      window.location.href = '/login';
      throw new Error('Authentication failed');
    }

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('API request failed:', error);
    throw error;
  }
}

// GET request helper
export function apiGet<T>(endpoint: string): Promise<T> {
  return apiRequest<T>(endpoint, { method: 'GET' });
}

// POST request helper
export function apiPost<T>(endpoint: string, data: any): Promise<T> {
  return apiRequest<T>(endpoint, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

// PUT request helper
export function apiPut<T>(endpoint: string, data: any): Promise<T> {
  return apiRequest<T>(endpoint, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

// DELETE request helper
export function apiDelete<T>(endpoint: string): Promise<T> {
  return apiRequest<T>(endpoint, { method: 'DELETE' });
}

// Authentication API functions
export async function login(
  username: string,
  password: string
): Promise<{ token: string; user: any }> {
  const loginRequest = {
    name: username,
    password: password,
  };

  try {
    const response = await fetch(`${API_BASE_URL}/users/login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(loginRequest),
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('Login failed:', response.status, errorText);
      throw new Error('Invalid credentials');
    }

    const data = await response.json();
    const token = data.access_token;
    const userInfo = decodeToken(token);

    setToken(token);

    return { token, user: userInfo };
  } catch (error) {
    console.error('Login error:', error);
    throw error;
  }
}

export async function getUser(userId: string): Promise<UserType> {
  return apiGet<UserType>(`/users/${userId}`);
}

export async function updateUser(
  userId: string,
  updateData: UpdateUserRequest
): Promise<UserType> {
  return apiRequest<UserType>(`/users/${userId}`, {
    method: 'PUT',
    body: JSON.stringify(updateData),
  });
}

export async function activateUser(
  userId: string,
  status: UserStatus
): Promise<UserType> {
  const path = status === UserStatus.ACTIVE ? 'activate' : 'deactivate';
  return apiRequest<UserType>(`/users/${userId}/${path}`, {
    method: 'PUT',
  });
}

export async function resetUserPassword(
  userId: string
): Promise<{ password: string }> {
  return apiRequest<{ password: string }>(`/users/${userId}/password`, {
    method: 'POST',
  });
}

// Flagged Products API functions
export async function getFlaggedProducts(
  page = 1,
  pageSize = 20
): Promise<FlaggedProductsResponse> {
  const params = new URLSearchParams({
    page: page.toString(),
    page_size: pageSize.toString(),
  });

  return apiGet<FlaggedProductsResponse>(`/products/flagged?${params}`);
}

export async function bulkUpdateFlaggedProducts(
  updates: UpdateNormalizedProductRequest[]
): Promise<void> {
  return apiPut<void>('/products/flagged/bulk-update', updates);
}
